import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [paginationNav] - The pagination nav element.
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Handles numbered pagination clicks: fetches the target page and swaps
   * grid + pagination nav in place, without a full reload or scroll jump.
   *
   * @param {Event} event
   */
  async onPaginationControlClick(event) {
    event.preventDefault();

    const anchor =
      event.currentTarget instanceof HTMLAnchorElement
        ? event.currentTarget
        : /** @type {HTMLElement} */ (event.target).closest('a');

    if (!anchor) return;

    const url = new URL(anchor.href);
    const { grid, paginationNav } = this.refs;

    if (!grid) return;

    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const newGrid = parsed.querySelector('[ref="grid"]');
    const newPaginationNav = parsed.querySelector('[ref="paginationNav"]');

    if (newGrid) {
      grid.replaceChildren(...newGrid.children);
    }

    if (paginationNav && newPaginationNav) {
      paginationNav.replaceChildren(...newPaginationNav.children);
    }

    history.pushState({}, '', url.toString());
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}